// ---------------------------------------------------------------------------
// OfflineShellNotice — when the device is offline AND the service worker has
// not yet cached the app shell, show a friendly explanation instead of the
// usual Pleasant boot screen / blank state. This is purely informational; the
// real cached-trip restoration is handled inside Index.tsx.
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { CloudOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";

const OFFLINE_READY_KEY = "pleasant.swOfflineReady.v1";

/** Marks the SW shell as cached. Called once `onOfflineReady` fires. */
export const markOfflineShellReady = () => {
  try {
    localStorage.setItem(OFFLINE_READY_KEY, "1");
  } catch {
    /* ignore */
  }
};

const isOfflineShellReady = () => {
  try {
    return localStorage.getItem(OFFLINE_READY_KEY) === "1";
  } catch {
    return false;
  }
};

export const OfflineShellNotice = () => {
  const online = useOnlineStatus();
  // Re-read on mount only — once true, stays true for the session.
  const [shellReady, setShellReady] = useState(() => isOfflineShellReady());

  useEffect(() => {
    if (shellReady) return;
    const onReady = () => setShellReady(true);
    window.addEventListener("pleasant:offline-ready", onReady);
    return () => window.removeEventListener("pleasant:offline-ready", onReady);
  }, [shellReady]);

  // Only render when offline and the shell never finished priming.
  if (online || shellReady) return null;

  return (
    <div
      role="alertdialog"
      aria-label="Offline assets unavailable"
      className="fixed inset-x-0 z-[80] flex justify-center px-4 pointer-events-none"
      style={{ bottom: "calc(env(safe-area-inset-bottom) + 5rem)" }}
    >
      <div className="pointer-events-auto max-w-sm w-full rounded-2xl border border-amber-300/70 dark:border-amber-700/60 bg-amber-50/95 dark:bg-amber-950/85 backdrop-blur shadow-soft p-3.5 text-amber-950 dark:text-amber-50">
        <div className="flex items-start gap-2.5">
          <CloudOff className="w-5 h-5 mt-0.5 shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">Offline assets aren't ready yet</div>
            <p className="text-xs opacity-90 mt-0.5">
              You haven't loaded Pleasant online on this device yet, so there's
              no cached copy to fall back to. Reconnect once and we'll save it
              for next time.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="h-8 mt-2 text-xs"
              onClick={() => window.location.reload()}
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
              Try again
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
